import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { WorldProvider } from 'koota/react';
import App from './App.tsx';
import { world } from './world.ts';

// On-screen error display for mobile debugging. Surfaces window
// errors and unhandled rejections in a fixed bottom overlay so iOS
// users can read them without needing dev tools. Earned its keep
// diagnosing the iOS-Safari brotli-DecompressionStream crash —
// without it we'd have been guessing. Lazy: the DOM node isn't
// created until something logs, so working users never see it.
//
// `?debug=1` in the URL additionally hooks `console.error`, useful
// when you suspect a library is logging swallowed exceptions. Off
// by default because Three.js / R3F log routine dev warnings there.
function installMobileErrorOverlay() {
  const debugMode =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('debug');
  const buffer: string[] = [];
  let box: HTMLDivElement | null = null;
  let logEl: HTMLDivElement | null = null;
  const ensureBox = () => {
    if (box) return box;
    box = document.createElement('div');
    box.id = '__mobile-error-overlay';
    Object.assign(box.style, {
      position: 'fixed',
      bottom: '0',
      left: '0',
      right: '0',
      maxHeight: '50vh',
      background: 'rgba(180, 30, 30, 0.94)',
      color: '#fff',
      font: '12px/1.35 ui-monospace,monospace',
      zIndex: '9999',
      display: 'flex',
      flexDirection: 'column',
    } satisfies Partial<CSSStyleDeclaration>);
    const bar = document.createElement('div');
    Object.assign(bar.style, {
      display: 'flex',
      gap: '8px',
      padding: '6px 8px',
      background: 'rgba(0,0,0,0.25)',
      flex: '0 0 auto',
    } satisfies Partial<CSSStyleDeclaration>);
    const mkBtn = (label: string, onClick: () => void) => {
      const b = document.createElement('button');
      b.textContent = label;
      Object.assign(b.style, {
        font: 'inherit',
        background: '#fff',
        color: '#900',
        border: '0',
        borderRadius: '4px',
        padding: '4px 10px',
        fontWeight: '600',
      } satisfies Partial<CSSStyleDeclaration>);
      b.addEventListener('click', onClick);
      return b;
    };
    bar.appendChild(mkBtn('Copy all', async () => {
      try {
        await navigator.clipboard.writeText(buffer.join('\n'));
        bar.querySelector('span')!.textContent = 'Copied!';
      } catch {
        bar.querySelector('span')!.textContent = 'Copy failed — long-press text below';
      }
    }));
    bar.appendChild(mkBtn('Clear', () => {
      buffer.length = 0;
      if (logEl) logEl.textContent = '';
    }));
    bar.appendChild(mkBtn('Hide', () => { if (box) box.style.display = 'none'; }));
    const status = document.createElement('span');
    status.style.marginLeft = 'auto';
    status.style.alignSelf = 'center';
    bar.appendChild(status);
    box.appendChild(bar);
    logEl = document.createElement('div');
    Object.assign(logEl.style, {
      overflow: 'auto',
      flex: '1 1 auto',
      padding: '6px 10px',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
      userSelect: 'text',
      webkitUserSelect: 'text',
    } satisfies Partial<CSSStyleDeclaration>);
    box.appendChild(logEl);
    document.body.appendChild(box);
    return box;
  };
  const formatErr = (payload: unknown): string => {
    if (typeof payload === 'string') return payload;
    if (payload && typeof payload === 'object') {
      const err = payload as { name?: string; message?: string; stack?: string };
      const head = [err.name, err.message].filter(Boolean).join(': ');
      const stack = err.stack;
      if (head && stack) {
        // Safari's stack often omits the message line — prepend it ourselves.
        return stack.startsWith(head) ? stack : `${head}\n${stack}`;
      }
      if (head) return head;
      if (stack) return stack;
      try {
        return JSON.stringify(payload, null, 2);
      } catch {
        return String(payload);
      }
    }
    return String(payload);
  };
  const log = (label: string, payload: unknown) => {
    ensureBox();
    const text = formatErr(payload);
    const line = `[${new Date().toISOString().slice(11, 23)}] ${label}: ${text}`;
    buffer.push(line);
    if (logEl) {
      const div = document.createElement('div');
      div.textContent = line;
      div.style.marginBottom = '6px';
      logEl.appendChild(div);
      logEl.scrollTop = logEl.scrollHeight;
    }
  };
  window.addEventListener('error', e =>
    log('error', e.error ?? e.message ?? '(no message)')
  );
  window.addEventListener('unhandledrejection', e =>
    log('rejection', e.reason)
  );
  if (debugMode) {
    const origError = console.error;
    console.error = (...args) => {
      try {
        log(
          'console.error',
          args
            .map(a => (typeof a === 'string' ? a : (a as Error)?.stack ?? JSON.stringify(a)))
            .join(' ')
        );
      } catch {
        /* ignore */
      }
      origError.apply(console, args);
    };
  }
}
installMobileErrorOverlay();

// No SpacetimeDBProvider here — connection setup is owned by SessionGuard
// (mounted by App when the route is /play). The landing page must NEVER
// open a WebSocket; mounting the STDB provider at the top would break that.

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <WorldProvider world={world}>
      <App />
    </WorldProvider>
  </StrictMode>
);
